import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth0/session";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Global platform settings — one row, ADMIN-only.
 *
 * Starts with the PAL operator-app install link: the URL an operator receives
 * in their invite to install the app (currently a Diawi build, later
 * TestFlight / a store link). Editing it here avoids a deploy every time the
 * build target changes.
 */
export interface PlatformSettings {
  palInstallUrl: string | null;
  updatedByUserId: string | null;
  updatedAt: string;
}

export interface PlatformSettingsInput {
  /** A valid URL, or `null`/`""` to clear. Non-URL → 400 from the server. */
  palInstallUrl?: string | null;
}

export function useGetPlatformSettings() {
  const { token } = useSession();
  return useQuery({
    queryKey: ["platform-settings"],
    enabled: !!token,
    queryFn: async () => {
      const { data } = await axios.get<{ data: PlatformSettings }>(
        `${API_URL}/api/v2/admin/platform-settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
  });
}

export function useUpdatePlatformSettings(options?: {
  onSuccess?: (data: PlatformSettings) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: PlatformSettingsInput
    ): Promise<PlatformSettings> => {
      const { data } = await axios.patch<{ data: PlatformSettings }>(
        `${API_URL}/api/v2/admin/platform-settings`,
        input,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["platform-settings"] });
      options?.onSuccess?.(data);
    },
    onError: (err) => options?.onError?.(err as Error),
  });
}

/**
 * Pull the server's rejection message out of an axios error, falling back to a
 * generic line. The PATCH validates the URL and returns 400 with a message
 * body — we surface that verbatim rather than inventing our own copy.
 */
export function platformSettingsErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as
      | { message?: string; error?: string }
      | undefined;
    return body?.message ?? body?.error ?? err.message;
  }
  return err instanceof Error ? err.message : "Something went wrong";
}
